/**
 * useRecentTeamMembers Hook
 *
 * Fetches members of a Microsoft Team in a managed tenant.
 *
 * Uses an in-memory cache to prevent refetching when navigating between pages.
 */

import { useState, useEffect, useCallback } from 'react';
import { TenantApiService } from '../services/tenantApi';
import type { TeamMember } from '../types/sharepoint';

interface UseRecentTeamMembersOptions {
  tenantId: string;
  teamId: string;
  enabled?: boolean;
}

interface UseRecentTeamMembersResult {
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface CacheEntry {
  members: TeamMember[];
  timestamp: number;
}

// Module-level cache to persist across component mounts
const teamMembersCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes (team membership changes less frequently)

/**
 * Hook to fetch members of a Microsoft Team.
 *
 * @param options.tenantId - The managed tenant ID
 * @param options.teamId - The Microsoft Team ID
 * @param options.enabled - Whether to fetch members (default: true)
 * @returns Object with members, loading state, error, and refetch function
 */
export function useRecentTeamMembers({
  tenantId,
  teamId,
  enabled = true,
}: UseRecentTeamMembersOptions): UseRecentTeamMembersResult {
  // Create cache key
  const cacheKey = `members:${tenantId}:${teamId}`;
  const cachedEntry = tenantId && teamId ? teamMembersCache.get(cacheKey) : undefined;
  const isCacheValid = cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_TTL_MS;

  const [members, setMembers] = useState<TeamMember[]>(isCacheValid ? cachedEntry.members : []);
  const [isLoading, setIsLoading] = useState<boolean>(!isCacheValid && enabled && !!tenantId && !!teamId);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async (skipCache = false) => {
    // Don't fetch if disabled or missing required IDs
    if (!enabled || !tenantId || !teamId) {
      setMembers([]);
      setIsLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true for manual refresh)
    if (!skipCache) {
      const cached = teamMembersCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
        setMembers(cached.members);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedMembers = await TenantApiService.getTeamMembers(tenantId, teamId);
      setMembers(fetchedMembers);
      // Update cache
      teamMembersCache.set(cacheKey, { members: fetchedMembers, timestamp: Date.now() });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch team members';
      setError(errorMessage);
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, teamId, enabled, cacheKey]);

  // Fetch members on mount and when dependencies change
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Manual refetch bypasses cache
  const refetch = useCallback(async () => {
    await fetchMembers(true);
  }, [fetchMembers]);

  return {
    members,
    isLoading,
    error,
    refetch,
  };
}
